import { useElapsedTime } from '@lib/hooks/useElapsedTime';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { WarmTransferContext } from '../types';

/** Duration in milliseconds to show "Connecting..." status before switching to "Ringing..." */
const WARM_TRANSFER_CONNECTION_DURATION_MS = 6000; // 6 seconds
/** Failsafe duration in milliseconds to re-enable buttons regardless of warm transfer state */
const WARM_TRANSFER_FAILSAFE_DURATION_MS = 60000; // 60 seconds (1 minute) to reenable buttons no matter what

/**
 * A React hook that manages warm transfer state and UI behavior
 *
 * This hook provides state management for warm transfer functionality, including:
 * - Connection status tracking and display text
 * - Button disable/enable logic during transfer process
 * - Feature flag integration for warm transfer availability
 * - Failsafe mechanisms to prevent UI lockup
 *
 * @param warmTransferContext - The warm transfer context containing transfer state and metadata
 * @param warmTransferContext.initiatedAt - Timestamp when the warm transfer was initiated
 * @param warmTransferContext.isWarmUpCallConnected - Whether the warm-up call is currently connected
 *
 * @returns An object containing warm transfer state and UI control flags
 * @returns warmTransferInProgress - Whether a warm transfer is currently in progress
 * @returns warmTransferConnectionStatus - Display text for connection status ("Connecting..." or "Ringing...")
 * @returns warmTransferConnected - Whether the warm transfer call is currently connected
 * @returns disableButtonsWhenWarmTransferConnecting - Whether UI buttons should be disabled during connection
 *
 * @example
 * ```typescript
 * const warmTransferContext = {
 *   initiatedAt: new Date(),
 *   isWarmUpCallConnected: false
 * };
 *
 * const {
 *   warmTransferInProgress,
 *   warmTransferConnectionStatus,
 *   warmTransferConnected,
 *   disableButtonsWhenWarmTransferConnecting
 * } = useWarmTransfer(warmTransferContext);
 *
 * // Use in UI
 * if (warmTransferInProgress) {
 *   console.log(`Status: ${warmTransferConnectionStatus}`);
 * }
 *
 * // Disable buttons during connection
 * <button disabled={disableButtonsWhenWarmTransferConnecting}>
 *   Transfer Call
 * </button>
 * ```
 */
export const useWarmTransfer = (
  warmTransferContext: WarmTransferContext | undefined,
) => {
  const {
    [FeatureFlagKeys.featureInboxCallWarmTransfers]:
      featureInboxCallWarmTransfers,
  } = useFeatureFlags();

  const warmTransferInProgress =
    !!featureInboxCallWarmTransfers && !!warmTransferContext;

  const warmTransferElapsedTimeMS = useElapsedTime({
    startTime: warmTransferContext?.initiatedAt
      ? new Date(warmTransferContext.initiatedAt).getTime()
      : 0,
    enabled: warmTransferInProgress,
  });

  // Determine the warm transfer connection text while connecting
  const warmTransferConnectionStatus =
    warmTransferElapsedTimeMS < WARM_TRANSFER_CONNECTION_DURATION_MS
      ? 'Connecting...'
      : 'Ringing...';

  // Determine if warm transfer is connected
  const warmTransferConnected = !!warmTransferContext?.isWarmUpCallConnected;

  // Determine if buttons should be disabled when warm transfer is connecting
  const disableButtonsWhenWarmTransferConnecting =
    warmTransferInProgress &&
    !warmTransferConnected &&
    warmTransferElapsedTimeMS < WARM_TRANSFER_FAILSAFE_DURATION_MS;

  return {
    warmTransferInProgress,
    warmTransferConnectionStatus,
    warmTransferConnected,
    disableButtonsWhenWarmTransferConnecting,
  };
};
